import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Flex, FlexColumn } from '../../Flex';
import { pluralizeString } from '../../../utils/common';
import { ToolInvocationEvent } from '../types';
import { ToolText } from './toolDisplay';
import ToolCallChip from './ToolCallChip';

const AUTO_COLLAPSE_DELAY_MS = 800;

const ArrowIcon = styled(KeyboardArrowDownRoundedIcon)<{
  $expanded: boolean;
}>`
  transition: transform 0.2s ease, opacity 0.2s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? '0deg' : '-90deg')});
  font-size: 18px;
  color: ${({ theme }) => theme.palette.common.gray4};
  opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
`;

const HeaderRow = styled(Flex)`
  align-items: center;
  gap: 8px;
  width: fit-content;
  cursor: pointer;
  user-select: none;

  &:hover ${ArrowIcon} {
    opacity: 1;
  }
`;

const ToolList = styled(FlexColumn)`
  gap: 2px;
  margin-left: 9px;
  padding-left: 16px;
  border-left: 1px solid ${({ theme }) => theme.palette.common.gray5};
`;

const ErrorText = styled.span`
  color: ${({ theme }) => theme.palette.error.main};
`;

type Props = {
  invocations: ToolInvocationEvent['toolInvocation'][];
};

const ToolCallGroup = ({ invocations }: Props) => {
  const [done, setDone] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const timerStartedRef = useRef(false);

  const latestByToolCallId = Object.values(
    invocations.reduce<Record<string, ToolInvocationEvent['toolInvocation']>>(
      (acc, inv) => ({ ...acc, [inv.toolCallId]: inv }),
      {},
    ),
  );

  const allDone = latestByToolCallId.every((inv) => inv.state === 'result');
  const errorCount = latestByToolCallId.filter(
    (inv) => inv.result?.isError,
  ).length;
  const count = latestByToolCallId.length;

  useEffect(() => {
    if (allDone && !timerStartedRef.current) {
      timerStartedRef.current = true;
      const timer = setTimeout(() => setDone(true), AUTO_COLLAPSE_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [allDone]);

  const singleGoal =
    count === 1
      ? (latestByToolCallId[0]?.args?.goal as string | undefined)
      : undefined;

  const baseLabel = done
    ? pluralizeString(count, 'tool', 'tools')
    : singleGoal ?? `Running ${pluralizeString(count, 'tool', 'tools')}...`;

  const errorLabel =
    done && errorCount > 0
      ? pluralizeString(errorCount, 'error', 'errors')
      : null;

  const HeaderIcon = done
    ? errorCount > 0
      ? ErrorRoundedIcon
      : CheckCircleRoundedIcon
    : SearchRoundedIcon;

  return (
    <FlexColumn>
      <HeaderRow onClick={() => setExpanded((prev) => !prev)}>
        <HeaderIcon sx={{ fontSize: '18px', color: 'common.magenta' }} />
        <ToolText $running={!done}>
          {baseLabel}
          {errorLabel && (
            <>
              {' · '}
              <ErrorText>{errorLabel}</ErrorText>
            </>
          )}
        </ToolText>
        <ArrowIcon $expanded={expanded} />
      </HeaderRow>
      {expanded && (
        <ToolList>
          {latestByToolCallId.map((inv) => (
            <ToolCallChip key={inv.toolCallId} invocation={inv} />
          ))}
        </ToolList>
      )}
    </FlexColumn>
  );
};

export default ToolCallGroup;
